import { msg } from '@lingui/core/macro';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { isKnownOutdated } from '#features/app-update/utils/outdatedState';
import { CLIENT_OUTDATED_CODE } from '#features/app-update/utils/schemaDrift';
import { requestChunkReload } from '#features/error/utils/chunkReload';
import { i18n } from '#features/i18n/i18n';
import { hasConnectionTrouble } from '#features/network/model/connectivity';
import { reconnectRefetchByCatchUp } from '#features/network/utils/reconnectRefetch';
import { toaster } from '#features/ui/components/Toaster';
import { envConfig } from '#constants/envConfig';
import { ApiError } from './ApiError';
import { isIOSApp } from './appCheck';
import { isAbortedRequest, isNetworkFailure } from './errorText';
import { reportActionError } from './reportError';
import { markErrorShownToUser } from './shownErrors';

/** Код отказа капчи от шлюза; приходит в `extensions.errorCode`, см. `gqlFetcher`. */
const CAPTCHA_ERROR_CODE = 'captcha.error';

/**
 * Отказы со своим сценарием: вход, бан и запрет прав уводят человека на другой экран, и тост
 * поверх этого перехода только мешает. Их показывает не тостер, а сам переход.
 */
const SELF_HANDLED_ERROR_CODES = new Set(['user.not.auth', 'user.banned', 'permission.denied']);

/**
 * Шлюз отклонил документ целиком — клиент разошёлся со схемой (см. `schemaDrift`).
 *
 * Реакция разная по платформам, и это не косметика. В приложении веб-бандл запечён в APK:
 * починить его нечем, кроме обновления из магазина. В браузере ровно та же ошибка означает
 * противоположное — у человека открыта вкладка со старой сборкой, и лечится она перезагрузкой,
 * а звать его в магазин бессмысленно. Один код ошибки, две ветки.
 *
 * В приложении ещё и текст выбирается по факту: «обновитесь» говорим, только если конфиг
 * действительно знает про более свежую версию. Иначе это наш баг в свежей сборке, и отправлять
 * человека обновляться туда, где обновляться не на что, — враньё.
 */
/**
 * Один и тот же отказ прилетает столько раз, сколько запросов было на экране, а `toaster.create`
 * без явного id складывает их стопкой (у тостера не настроены ни `max`, ни дедуп). Свой id
 * схлопывает их в один: сообщение здесь всегда одно и то же, второй экземпляр ничего не добавляет.
 */
const OUTDATED_TOAST_ID = 'client-outdated';

const handleClientOutdated = async () => {
  // Это тост по факту запроса, а не экран загрузки, — каталог переводов здесь уже активен,
  // поэтому обычные макросы, а не plain-строки (в отличие от самого экрана обновления).
  const retryLater = () =>
    i18n._(
      msg({
        id: 'common:client.error.retryLater',
        message: 'Something went wrong on our side. Please try again in a moment.',
      })
    );

  if (envConfig.IS_NATIVE) {
    toaster.create({
      id: OUTDATED_TOAST_ID,
      title: isKnownOutdated()
        ? i18n._(
            msg({
              id: 'common:client.outdated.app',
              message: 'Your app version is out of date. Please update it.',
            })
          )
        : retryLater(),
      type: 'error',
    });
    return;
  }

  if (!envConfig.IN_BROWSER) {
    return;
  }

  // Тот же путь, что у протухших чанков: решение одно на вкладку, с кулдауном и проверкой,
  // что сервер действительно отдаёт другую сборку. Если перезагрузку назначить нельзя,
  // человек обязан увидеть хоть что-то — молчание здесь было бы регрессом.
  const scheduled = await requestChunkReload({
    storage: window.sessionStorage,
    reload: () => window.location.reload(),
  });

  if (!scheduled) {
    toaster.create({
      id: OUTDATED_TOAST_ID,
      title: i18n._(
        msg({
          id: 'common:client.outdated.reload',
          message: 'The page is out of date. Please reload it.',
        })
      ),
      type: 'error',
    });
  }
};

export const createQueryClient = () => {
  const queryClient = new QueryClient({
    /**
     * Единственная страховка на все ~70 вызовов `mutateAsync` в приложении: отказ обязан быть
     * виден человеку, а не только в Sentry.
     *
     * Начиналось это с капчи. Её проверка живёт на инъекции аргумента в шлюзе, то есть до
     * резолвера, и приходит не членом union'а `GlobalError`, а записью в `errors[]` — формы её не
     * ждут и не катчат, `mutateAsync` реджектится в никуда, и кнопка выглядит сломанной. Один
     * обработчик закрыл все девять форм с капчей (вход, регистрация, сброс и смена пароля, смена
     * почты, удаление аккаунта, жалоба, фидбек) — по той же причине, по которой девять try/catch
     * разъехались бы при первой же новой форме.
     *
     * Ровно так же молчала и любая другая мутация без своего `onError`, поэтому теперь обработчик
     * общий. Заодно он помечает показанный отказ (`shownErrors`): `mutateAsync` отклоняет промис
     * даже когда ошибка уже на экране, и без пометки Sentry записывал бы это падением приложения.
     *
     * ⚠️ `setupRouterSsrQueryIntegration` (src/router.tsx) позже подменяет
     * `getMutationCache().config`, но свой `onError` он вызывает первым и затем делегирует в
     * этот — цепочка сохраняется, порядок значения не имеет.
     */
    /**
     * Намеренно узкий обработчик: только расхождение клиента со схемой.
     *
     * Общего `onError` у чтений до сих пор не было, и заводить его широким нельзя — тост
     * выскакивал бы на каждый фоновый refetch и на каждую отменённую навигацию. А вот этот
     * конкретный отказ до человека доходить обязан: он означает, что приложение (или вкладка)
     * больше не разговаривает с сервером, и без реакции экран просто остаётся пустым.
     */
    queryCache: new QueryCache({
      onError: (error) => {
        // Пока сети нет или сервер не отвечает, о сбоях чтения говорит полоса под шапкой
        // (`network/components/OfflineBanner`); тост поверх неё повторял бы то же самое на каждый
        // упавший запрос. Мутации ниже это не касается: там человек сам нажал кнопку.
        //
        // Для GlitchTip такой отказ тоже показан. Если он всплывёт дальше - лоадер маршрута,
        // `fetchQuery` без обработчика, - это не падение приложения, а отсутствие сети, и
        // `demoteShownErrorEvent` запишет его предупреждением, а не ошибкой.
        if (hasConnectionTrouble()) {
          if (isNetworkFailure(error)) {
            markErrorShownToUser(error);
          }
          return;
        }

        if (error instanceof ApiError && error.errorCode === CLIENT_OUTDATED_CODE) {
          void handleClientOutdated();
          markErrorShownToUser(error);
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (isAbortedRequest(error)) {
          return;
        }

        // Фоновая отметка, о которой человек не просил и которой не ждёт (досылка пояса, счётчик
        // копирования ссылки, синхронизация языка): сбой ей не о чем объявлять. Помечаем
        // показанным, чтобы Sentry не принимал отклонённый `mutateAsync` за падение.
        if (mutation.meta?.silent === true) {
          markErrorShownToUser(error);
          return;
        }

        if (error instanceof ApiError) {
          if (error.errorCode === CAPTCHA_ERROR_CODE) {
            toaster.create({
              title: isIOSApp()
                ? i18n._(
                    msg({
                      id: 'common:appCheck.rejected',
                      message: 'Could not verify the app. Restart Teamplay and try again.',
                    })
                  )
                : i18n._(
                    msg({
                      id: 'common:captcha.rejected',
                      message:
                        'Verification failed. Reload the page and try again – if you use a VPN or a proxy, turn it off for this site.',
                    })
                  ),
              type: 'error',
            });
            markErrorShownToUser(error);
            return;
          }

          if (error.errorCode === CLIENT_OUTDATED_CODE) {
            void handleClientOutdated();
            markErrorShownToUser(error);
            return;
          }

          if (error.errorCode && SELF_HANDLED_ERROR_CODES.has(error.errorCode)) {
            markErrorShownToUser(error);
            return;
          }
        }

        // Нет сети - единственный отказ, о котором говорит общий обработчик даже тогда, когда у
        // мутации есть свой `onError`. Свой текст («Не удалось сменить…») в этот момент только
        // повторил бы то же самое второй плашкой: тостер не пропускает ошибки, пока открыто окно
        // после общего тоста (см. `errorToastGate`), так что у человека остаётся одна фраза - что
        // связи нет, - а свой `onError` по-прежнему откатывает оптимистичное состояние.
        //
        // У мутации со своим onError остальные отказы показывает она сама, своими словами и на
        // своём месте. Второй тост поверх этого был бы дублем.
        if (!isNetworkFailure(error) && mutation.options.onError) {
          markErrorShownToUser(error);
          return;
        }

        // Без своего обработчика отказ до сих пор не доходил до человека вообще: тост капчи
        // выше был единственным на весь кэш, остальные мутации молча ничего не делали, и кнопка
        // выглядела сломанной. Тем же путём идут и ошибки, не бывшие `ApiError`: сломанный
        // `mutationFn`, не-GraphQL загрузка.
        reportActionError(error);
      },
    }),
    defaultOptions: {
      queries: {
        // https://tanstack.com/query/latest/docs/framework/react/reference/useQuery
        retry: (failureCount, error) => {
          if (error instanceof ApiError) {
            // Retry on network errors, maintenance (503), and transient upstream
            // blips (502 bad gateway / 504 gateway timeout) from CF→origin.
            if (
              error.status === 0 ||
              error.status === 502 ||
              error.status === 503 ||
              error.status === 504
            ) {
              // On the server, don't retry — fail fast so the SSR response becomes
              // 503 and nginx serves maintenance.html instead of stalling the shell.
              // The client still retries (degraded UI, no full-page takeover).
              if (envConfig.IN_SERVER) {
                return false;
              }
              return failureCount < 3;
            }
            return false;
          }

          if (error instanceof Error && error.name === 'TypeError') {
            return failureCount < 2;
          }

          return false;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
        refetchOnWindowFocus: false,
        // По возвращении сети экран перечитывает `catchUp` - один раз, как бы о возвращении ни
        // узнали (сеть, сокет, событие `online`). Свой перезапрос react-query был бы вторым
        // проходом. Явный `false` у запроса по-прежнему запрещает и тот, и другой.
        refetchOnReconnect: reconnectRefetchByCatchUp,
        staleTime: 0, // 1000 * 60 * 5,
      },
      mutations: {
        // Never auto-retry mutations — request may have reached the server,
        // retrying could create duplicates. Show error, let user retry manually.
        retry: false,
        // Без сети мутация не встаёт на паузу до её возвращения, как по умолчанию в react-query, а
        // сразу падает с "нет соединения" и показывает тост выше. Человек нажал "Отправить" и
        // должен узнать, что ничего не ушло, а не смотреть на крутилку. Отправка через десять
        // минут, когда сеть вернулась, - тот же непрошеный повтор, от которого защищает `retry`.
        networkMode: 'always',
      },
    },
  });

  return queryClient;
};

export const queryClient = createQueryClient();
